import type { FormDialogRef } from '@/components'
import {
  Button,
  Checkbox,
  FAIcon,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FormDialog,
  Input,
  NumberInput,
  Radios,
} from '@/components'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { initiallyShown } from './generation-modifier-builder'
import type { GenerationModifierForm } from './generation-modifier-form'
import {
  defaultValues,
  generationModifierSchema,
  toInput,
} from './generation-modifier-form'
import { ModifierConditions, ModifierEffects } from './generation-modifier-rows'
import { useGenerationRuleLabels } from './generation-rule-labels'
import {
  CREATE_SALARY_ITEM_GENERATION_MODIFIER_MUTATION,
  PATCH_SALARY_ITEM_GENERATION_MODIFIER_MUTATION,
} from './mutations'
import type { GenerationModifierNode } from './queries'
import { ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY } from './queries'

function useGenerationModifierSchema() {
  return generationModifierSchema(
    useTranslate({
      nameRequired: 'settings.generation-modifiers.error-name',
      noEffect: 'settings.generation-modifiers.error-no-effect',
      noCondition: 'settings.generation-modifiers.error-no-condition',
      restAwardAmount: 'settings.generation-modifiers.error-rest-award-amount',
      perDurationAmount:
        'settings.generation-modifiers.error-per-duration-amount',
      durationOrder: 'settings.generation-modifiers.error-duration-order',
      dateOrder: 'settings.generation-modifiers.error-date-order',
    })
  )
}

interface Props {
  modifier?: GenerationModifierNode
  /** The salary setting a new modifier starts scoped to. */
  salaryWorkTimeId?: string
}

export const GenerationModifierDialog: React.FC<Props> = ({
  modifier,
  salaryWorkTimeId,
}) => {
  const startValues = () => ({
    ...defaultValues(modifier),
    ...(!modifier && salaryWorkTimeId
      ? { salaryWorkTime: salaryWorkTimeId }
      : {}),
  })
  const translations = useTranslate({
    add: 'settings.generation-modifiers.add',
    edit: 'settings.generation-modifiers.edit',
    name: 'common.name',
    kind: 'settings.generation-rules.kind',
    appliesTo: 'settings.generation-modifiers.applies-to',
    priority: 'common.priority',
    priorityDescription: 'settings.generation-modifiers.priority-description',
    active: 'common.active',
    beforeSaving: 'settings.generation-modifiers.before-saving',
  })
  const labels = useGenerationRuleLabels()
  const schema = useGenerationModifierSchema()

  const dialogRef = useRef<FormDialogRef>(null)
  const [error, setError] = useState<string | null>(null)
  const [shown, setShown] = useState(() => initiallyShown(startValues()))

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: startValues(),
    mode: 'onChange',
  })

  // Confirm stays disabled while the form is invalid, and a missing piece is
  // often in a row the user has not touched, so say what is missing.
  const watched = useWatch({ control: form.control })
  const parsed = schema.safeParse(watched)
  const blockers = parsed.success
    ? []
    : [...new Set(parsed.error.issues.map(issue => issue.message))]

  const refetchQueries = [{ query: ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY }]
  const [createModifier] = useMutation(
    CREATE_SALARY_ITEM_GENERATION_MODIFIER_MUTATION,
    { refetchQueries, awaitRefetchQueries: true }
  )
  const [patchModifier] = useMutation(
    PATCH_SALARY_ITEM_GENERATION_MODIFIER_MUTATION,
    { refetchQueries, awaitRefetchQueries: true }
  )

  const kindOptions = (['diet', 'salary_day', 'both'] as const).map(value => ({
    value,
    label: labels.kind[value],
  }))
  const subjectOptions = (['work_period', 'rest', 'both'] as const).map(
    value => ({ value, label: labels.subject[value] })
  )

  function show(key: string) {
    setShown(current => new Set(current).add(key))
  }

  function hide(key: string) {
    setShown(current => {
      const next = new Set(current)
      next.delete(key)
      return next
    })
  }

  function resetForm() {
    setError(null)
    setTimeout(() => {
      form.reset(startValues())
      setShown(initiallyShown(startValues()))
    }, 300)
  }

  async function handleSubmit(values: GenerationModifierForm) {
    const input = toInput(values)
    try {
      if (modifier)
        await patchModifier({ variables: { id: modifier.id, input } })
      else await createModifier({ variables: { input } })
      dialogRef.current?.close()
    } catch (mutationError) {
      setError((mutationError as Error).message)
    }
  }

  return (
    <FormDialog
      ref={dialogRef}
      className="sm:max-w-2xl"
      form={form}
      error={error}
      trigger={
        modifier ? (
          <Button variant="ghost" size="icon-sm">
            <FAIcon icon="edit" />
          </Button>
        ) : (
          <Button size="sm">
            <FAIcon icon="plus" />
            {translations.add}
          </Button>
        )
      }
      header={modifier ? translations.edit : translations.add}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Controller
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <Field aria-invalid={fieldState.invalid}>
              <FieldLabel>{translations.name}</FieldLabel>
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.kind}</FieldLabel>
                <Radios
                  value={field.value}
                  options={kindOptions}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="appliesTo"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.appliesTo}</FieldLabel>
                <Radios
                  value={field.value}
                  options={subjectOptions}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />
        </div>

        <ModifierConditions
          form={form}
          shown={shown}
          onShow={show}
          onHide={hide}
        />
        <ModifierEffects
          form={form}
          shown={shown}
          onShow={show}
          onHide={hide}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            control={form.control}
            name="priority"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.priority}</FieldLabel>
                <FieldDescription>
                  {translations.priorityDescription}
                </FieldDescription>
                <NumberInput
                  value={field.value}
                  decimalScale={0}
                  onChange={value => field.onChange(value ?? 0)}
                />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <Field orientation="horizontal" className="sm:pt-7">
                <Checkbox
                  {...field}
                  id="generation-modifier-active"
                  size="md"
                />
                <FieldLabel htmlFor="generation-modifier-active">
                  {translations.active}
                </FieldLabel>
              </Field>
            )}
          />
        </div>

        {blockers.length > 0 && (
          <div className="rounded-lg bg-quaternary p-3 text-sm">
            <p className="font-medium">{translations.beforeSaving}</p>
            <ul className="list-disc pl-5">
              {blockers.map(message => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}
      </FieldGroup>
    </FormDialog>
  )
}
